import Modal from '../Modal';
import { useModal } from '../../../providers/ModalProvider';
import { modals } from '../../../providers/modals';
import { DropdownText } from '../../dropdowns/DropdownText';
import DropdownCombo from '../../dropdowns/DropdownCombo';
import { Text } from '../../typography';
import { Form } from 'react-bootstrap';
import { loader } from 'graphql.macro';
import { useMutation } from '@apollo/client';
import { keys } from '../../../utilities/translator/translation_keys';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect } from 'react';
import { useWorkspace } from '../../../providers/WorkspaceProvider';
import { dropdownTypes } from '../../dropdowns/dropdown';
import styles from './OpenLinkModal.module.scss';
import { useCurrentUser } from '../../../providers/UserProvider';
import { useTemplates } from '../../../providers/TemplateProvider';
import { useQueryParams } from '../../../hooks/misc';
import UniversalDropdown from '../../dropdownV2/UniversalDropdown';

const createOpenLinkMutation = loader('./OpenLinkModal.create.graphql');

export default function OpenLinkModal() {
  const { t } = useTranslation();
  const { closeModal, modalState, updateModal } = useModal();
  const { show, expires, action, refetch } = modalState.openLinkModal;
  const [createOpenLink] = useMutation(createOpenLinkMutation);
  const { allWorkspaces } = useWorkspace();
  const { allTemplates } = useTemplates();
  const { assessmentName } = useCurrentUser();
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const { upsertParam, deleteParam } = useQueryParams();

  const update = (variables) =>
    updateModal({ modalName: modals.openLink, variables: { ...variables } });

  const templates = useMemo(
    () =>
      allTemplates?.filter(
        (t) => t.templateType === 'EXTERNAL' && !t.isArchived,
      ),
    [allTemplates],
  );

  const actionOptions = useMemo(
    () => [
      {
        title: t(keys.openLink.REPORT_OBSERVATION),
        scopes: {
          queries: ['workspaces'],
          mutations: ['addObservation'],
        },
        pathname: 'addObservation',
      },
      {
        title: t(keys.openLink.SUBMIT_VARIABLE, {
          variable: assessmentName || t(keys.common.ASSESSMENT),
        }),
        scopes: {
          queries: [
            'inspectionTemplates',
            'simpleStorageUploadUrl',
            'workspaces',
          ],
          mutations: [
            'addInspection',
            'addInspectionItem',
            'updateInspection',
            'updateInspectionItem',
          ],
        },
        pathname: 'addAssessment',
      },
    ],
    [t, assessmentName],
  );

  const submitDisabled = useMemo(() => {
    if (action?.pathname === 'addAssessment') {
      return !action || !selectedWorkspace || !selectedTemplate;
    } else {
      return !action || !selectedWorkspace;
    }
  }, [action, selectedWorkspace, selectedTemplate]);

  useEffect(
    () =>
      action?.pathname === 'addAssessment'
        ? upsertParam('type', 'EXTERNAL')
        : deleteParam('type'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [action],
  );

  return (
    <Modal
      title={t(keys.action.CREATE_VARIABLE, {
        variable: t(keys.openLink.ACCESS_KEY),
      })}
      open={show}
      onClose={() => {
        closeModal({ modalName: modals.openLink });
        setSelectedTemplate(null);
        setSelectedWorkspace(null);
      }}
      submitText={t(keys.action.CREATE)}
      submitDisabled={submitDisabled}
      onSubmit={() =>
        createOpenLink({
          variables: {
            ...action,
            action: action?.title,
            expires,
            workspaceId: selectedWorkspace?.id,
            templateId: selectedTemplate?.id,
          },
        }).then(() => refetch())
      }
    >
      <div className={styles.container}>
        <Text noMargin weight="semiBold">
          {t(keys.common.WORKSPACE)}
        </Text>
        <DropdownCombo
          type={dropdownTypes.WORKSPACE}
          items={allWorkspaces}
          onChange={(w) => setSelectedWorkspace(w)}
          selected={selectedWorkspace}
        />
        <Text noMargin weight="semiBold">
          {t(keys.openLink.CHOOSE_ACTION)}
        </Text>
        <DropdownText
          onRemove={() => update({ action: null })}
          items={actionOptions}
          onChange={(value) => update({ action: value })}
          selected={action}
        />
        {action?.pathname === 'addAssessment' && (
          <>
            <Text noMargin weight="semiBold">
              {t(keys.common.TEMPLATE)}
            </Text>
            <UniversalDropdown
              type="template"
              options={[...(templates || [])].sort((a, b) =>
                a?.title?.localeCompare(b?.title),
              )}
              onChange={(newTemplate) => {
                setSelectedTemplate(newTemplate);
              }}
              selected={selectedTemplate}
              isRequired
              placeholder={`Select Template...`}
            />
            {templates?.length < 1 && (
              <Text noMargin size="sm" color="secondary">
                Only External type templates can be used
              </Text>
            )}
          </>
        )}
        <Text noMargin weight="semiBold">
          {t(keys.action.EXPIRES)}
        </Text>
        <Form.Control
          type={'date'}
          value={expires}
          onChange={({ target: { value } }) => update({ expires: value })}
        />
      </div>
    </Modal>
  );
}
